import axios from "axios";

const API_SERVER_HOST = process.env.REACT_APP_API_SERVER_HOST;

export const AUTH_EXPIRED_EVENT = "auth:expired";
const AUTH_EXPIRED_ERROR_FLAG = "__wewinAuthExpired";

const ACCESS_REISSUE_CODES = new Set([
  "ACCESS_JWT_EXPIRED",
  "JWT_ACCESS_DENIED",
]);

const REFRESH_EXPIRED_CODES = new Set([
  "REFRESH_JWT_EXPIRED",
  "JWT_REFRESH_TOKEN_NOT_FOUND",
  "JWT_REFRESH_TOKEN_NOT_MATCH",
  "INVALID_TOKEN",
]);

const AUTH_REQUIRED_CODES = new Set([
  ...REFRESH_EXPIRED_CODES,
  "ACCESS_JWT_NOT_FOUND",
  "JWT_ACCESS_TOKEN_NOT_FOUND",
  "JWT_TOKEN_NOT_FOUND",
  "ACCESS_TOKEN_NOT_FOUND",
  "TOKEN_NOT_FOUND",
  "AUTHENTICATION_REQUIRED",
  "LOGIN_REQUIRED",
  "LOGIN_INFO_NOT_FOUND",
  "UNAUTHORIZED",
]);

export const api = axios.create({
  baseURL: API_SERVER_HOST,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // HttpOnly 쿠키 인증이면 필수
});

const authApi = axios.create({
  baseURL: API_SERVER_HOST,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

let reissuePromise = null;
let alreadyNotifiedAuthExpired = false;

const getResponseCode = (error) => {
  const data = error?.response?.data;
  return data?.responseCode ?? data?.code ?? data?.errorCode ?? "";
};

const getResponseMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return (
    data?.message ||
    data?.msg ||
    data?.errorMessage ||
    data?.error ||
    data?.detail ||
    ""
  );
};

const isAccessReissueTarget = (error) => {
  return ACCESS_REISSUE_CODES.has(getResponseCode(error));
};

const isRefreshExpired = (error) => {
  return REFRESH_EXPIRED_CODES.has(getResponseCode(error));
};

const isAuthRequiredCode = (error) => {
  return AUTH_REQUIRED_CODES.has(getResponseCode(error));
};

const isAuthMissingMessage = (error) => {
  const message = getResponseMessage(error);
  if (!message) return false;

  return /로그인\s*정보.*없|로그인이\s*필요|인증\s*정보.*없|토큰.*없|token.*not\s*found|unauthorized/i.test(
    message
  );
};

const isAuthRedirectTarget = (error) => {
  if (error?.config?.skipAuthRedirect) return false;

  const status = error?.response?.status;
  return (
    status === 401 ||
    isAuthRequiredCode(error) ||
    isAuthMissingMessage(error)
  );
};

export const isAuthExpiredError = (error) => {
  return !!error?.[AUTH_EXPIRED_ERROR_FLAG];
};

const notifyAuthExpired = (reason) => {
  if (alreadyNotifiedAuthExpired) return;

  alreadyNotifiedAuthExpired = true;
  window.dispatchEvent(
    new CustomEvent(AUTH_EXPIRED_EVENT, {
      detail: { reason },
    })
  );
};

const markAuthExpired = (error, reason) => {
  if (error && typeof error === "object") {
    error[AUTH_EXPIRED_ERROR_FLAG] = true;
  }
  notifyAuthExpired(reason);
};

const reissue = () => {
  if (!reissuePromise) {
    reissuePromise = authApi.post("/auth/reissue").finally(() => {
      reissuePromise = null;
    });
  }

  return reissuePromise;
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (
      originalRequest &&
      !originalRequest._retry &&
      isAccessReissueTarget(error)
    ) {
      originalRequest._retry = true;

      try {
        await reissue();
        return api(originalRequest);
      } catch (reissueError) {
        if (
          !originalRequest.skipAuthRedirect &&
          (isRefreshExpired(reissueError) || isAuthRedirectTarget(reissueError))
        ) {
          markAuthExpired(reissueError, getResponseCode(reissueError));
        }

        return Promise.reject(reissueError);
      }
    }

    if (isAuthRedirectTarget(error)) {
      markAuthExpired(error, getResponseCode(error));
    }

    return Promise.reject(error);
  }
);
